import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LANGUAGES, LANGUAGE_LABELS, LANGUAGE_FLAGS, uiStrings } from "@bongeunsa/i18n";
import type { Language } from "@bongeunsa/i18n";
import { useLangStore } from "@/store/lang.js";

interface LanguageSwitcherProps {
  dark?: boolean;
}

export function LanguageSwitcher({ dark = false }: LanguageSwitcherProps) {
  const { lang, setLang } = useLangStore();
  const [open, setOpen] = useState(false);

  const triggerClass = dark
    ? "flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-sm transition-colors hover:bg-white/20"
    : "flex h-8 w-8 items-center justify-center rounded-full bg-black/5 text-sm transition-colors hover:bg-black/10";

  const dropdownClass = dark
    ? "absolute right-0 top-full z-50 mt-1.5 w-40 overflow-hidden rounded-xl bg-stone-800 shadow-xl ring-1 ring-white/10"
    : "absolute right-0 top-full z-50 mt-1.5 w-40 overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-black/5";

  const itemClass = (active: boolean) =>
    dark
      ? `flex w-full items-center gap-2.5 px-4 py-2.5 text-sm transition-colors hover:bg-stone-700 ${active ? "bg-stone-700 font-semibold text-amber-200" : "text-stone-300"}`
      : `flex w-full items-center gap-2.5 px-4 py-2.5 text-sm transition-colors hover:bg-gray-50 ${active ? "bg-gray-50 font-semibold text-gray-900" : "text-gray-700"}`;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={triggerClass}
        aria-label={uiStrings[lang].accessibility.selectLanguage}
      >
        <span>{LANGUAGE_FLAGS[lang]}</span>
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
              className={dropdownClass}
            >
              {LANGUAGES.map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => { setLang(l as Language); setOpen(false); }}
                  className={itemClass(l === lang)}
                >
                  <span>{LANGUAGE_FLAGS[l as Language]}</span>
                  <span>{LANGUAGE_LABELS[l as Language]}</span>
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
